import React, { useCallback } from 'react';
import { gql } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { useBlockingRouter } from '../../../../hooks/useBlockingRouter';
import { useToast } from '../../../../hooks/useToast';
import { ButtonBarButtonsProp } from '../../../ButtonBar';
import { Icon } from '../../../../blocks/icons';
import {
	InvoiceStatus,
	InvoiceViewButtonBarVoidButtonInvoiceFragment,
	useInvoiceViewButtonBarVoidButton_MarkInvoiceVoidMutation,
} from '#types';
import { displayConfirm } from '../../../../redux/actions';
import { FlattenedUser } from '../../../../types/user';
import rpcShared from '@rockpapercoin/rpc-shared';
import { client } from '../../../../context/ApolloContextProvider/client';

export const invoiceViewButtonBarVoidButtonInvoiceFragment = gql`
	fragment invoiceViewButtonBarVoidButtonInvoice on Invoice {
		id
		status
		organization {
			...getViewingUserDocumentRelationshipOrganization
		}
	}
	${ rpcShared.organizations
		.getViewingUserDocumentRelationshipOrganizationFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InvoiceViewButtonBarVoidButton_markInvoiceVoid = gql`
	mutation InvoiceViewButtonBarVoidButton_markInvoiceVoid(
		$where: InvoiceWhereUniqueInput!
	) {
		markInvoiceVoid(where: $where) {
			id
			status
		}
	}
`;

type UseInvoiceViewButtonBarVoidButtonReturnType = {
	button: ButtonBarButtonsProp['buttons'];
};

type UseInvoiceViewButtonBarVoidButtonProps = {
	invoice?: InvoiceViewButtonBarVoidButtonInvoiceFragment;
	requestingUserBaseId?: string;
};

export const useInvoiceViewButtonBarVoidButton = ( {
	invoice,
	requestingUserBaseId,
}: UseInvoiceViewButtonBarVoidButtonProps ): UseInvoiceViewButtonBarVoidButtonReturnType => {
	const { showError, showSuccess } = useToast();
	const router = useBlockingRouter();
	const [ voidInvoice, { loading } ] =
		useInvoiceViewButtonBarVoidButton_MarkInvoiceVoidMutation();

	const { viewingUserIsAdminOrManager: viewingUserIsVendorAdminOrManager } =
		rpcShared.organizations.getViewingUserDocumentRelationship( {
			organization: invoice?.organization,
			viewingUserId: requestingUserBaseId,
		} );

	const invoiceCanBeVoided =
		invoice?.status &&
		[ InvoiceStatus.Draft, InvoiceStatus.Sent ].includes( invoice.status );
	const dispatch = useDispatch();
	const user: FlattenedUser = useSelector( ( state: any ) => state?.user );

	const handleVoid = useCallback( async () => {
		const voidInvoiceCallback = async () => {
			if ( invoice?.id ) {
				const { errors } = await voidInvoice( {
					variables: { where: { id: invoice.id } },
				} );

				if ( errors ) {
					client.refetchQueries( { include: [ 'invoiceDetail_getInvoiceWhere' ] } );
					errors.map( ( error ) => showError( error ) );
				} else {
					showSuccess( 'Invoice voided' );
					router.push( '/invoices' );
				}
			}
		};
		dispatch(
			displayConfirm( {
				active: true,
				props: {
					title: 'Are you sure?',
					message: 'Voiding an invoice cannot be undone',
					options: [
						{
							text: 'Cancel',
							className: 'button--ghost',
							callback: () => dispatch( displayConfirm( { active: false } ) ),
						},
						{
							text: 'Void',
							callback: () => {
								dispatch( displayConfirm( { active: false } ) );
								voidInvoiceCallback();
							},
						},
					],
				},
			} )
		);
	}, [
		dispatch,
		invoice?.id,
		voidInvoice,
		showError,
		showSuccess,
		router
	] );

	return {
		button: {
			Void: {
				isVisible: viewingUserIsVendorAdminOrManager && user?.isLoggedIn,
				color: 'secondary',
				'data-cy': 'void-button',
				isIconWhenSmall: true,
				disabled: loading || !invoice?.id || !invoiceCanBeVoided,
				startIcon: <Icon type='decline' />,
				onClick: handleVoid,
			},
		},
	};
};
